import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { generateFixidSerial } from "@/lib/generateFixidSerial";

export interface ProductPassport {
  id: string;
  serial_number: string;
  appliance_id: string;
  work_order_id: string | null;
  user_id: string;
  workflow_type: "repair" | "refurbish";
  brand: string | null;
  model_number: string | null;
  category: string | null;
  appliance_name: string | null;
  quality_grade: string | null;
  summary_data: Record<string, any> | null;
  completed_at: string;
  created_at: string;
}

interface CreatePassportInput {
  applianceId: string;
  workOrderId?: string | null;
  workflowType: "repair" | "refurbish";
  brand?: string | null;
  modelNumber?: string | null;
  category?: string | null;
  applianceName?: string | null;
  qualityGrade?: string | null;
  summaryData?: Record<string, any>;
}

/**
 * Look up an existing passport for a given work order id.
 */
export function usePassportForWorkOrder(workOrderId: string | undefined | null) {
  const [passport, setPassport] = useState<ProductPassport | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!workOrderId) {
      setPassport(null);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("product_passports")
      .select("*")
      .eq("work_order_id", workOrderId)
      .maybeSingle();
    if (!error && data) setPassport(data as ProductPassport);
    setLoading(false);
  }, [workOrderId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { passport, loading, refresh };
}

/**
 * Look up a passport by id (used by the public passport page).
 * Falls back to the most recent passport for the appliance id if no passport row matches.
 */
export function usePassportById(id: string | undefined) {
  const [passport, setPassport] = useState<ProductPassport | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) {
        setLoading(false);
        setNotFound(true);
        return;
      }
      setLoading(true);
      // Try by passport id
      const { data: byId } = await supabase
        .from("product_passports")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (cancelled) return;
      if (byId) {
        setPassport(byId as ProductPassport);
        setLoading(false);
        return;
      }
      // Fallback: treat id as appliance id (legacy URL)
      const { data: byAppliance } = await supabase
        .from("product_passports")
        .select("*")
        .eq("appliance_id", id)
        .order("completed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (byAppliance) {
        setPassport(byAppliance as ProductPassport);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { passport, loading, notFound };
}

/**
 * Create a new passport row with a unique FixID serial. Retries on serial collision.
 */
export async function createPassport(input: CreatePassportInput): Promise<ProductPassport> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error("createPassport: getSession failed", sessionError);
    throw new Error("Authentication check failed. Please sign in again.");
  }
  const userId = sessionData?.session?.user?.id;
  if (!userId) throw new Error("You must be signed in to generate a passport.");

  const companyId = await fetchCurrentCompanyId(userId);
  if (!companyId) throw new Error("You're not assigned to a company yet — contact your admin.");

  if (!input.applianceId) throw new Error("Missing appliance id");

  // Resolve a real UUID for appliance_id. The id may be a temporary
  // "scanned-..." identifier or another non-UUID placeholder. In that
  // case, try to recover the real UUID from the work order, then fall
  // back to upserting an appliance row by (user_id, model_number).
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  let resolvedApplianceId = input.applianceId;
  if (!UUID_RE.test(resolvedApplianceId)) {
    // Client-side guard: must have either a work order or a model number to recover/persist.
    const hasWorkOrder = !!input.workOrderId;
    const hasModelNumber = !!(input.modelNumber && input.modelNumber.trim());
    if (!hasWorkOrder && !hasModelNumber) {
      throw new Error(
        "Cannot generate passport: this appliance hasn't been saved yet and no model number is available. Please add a model number or start a work order first."
      );
    }

    // 1) Try via work order
    if (input.workOrderId) {
      const { data: wo } = await supabase
        .from("work_orders")
        .select("appliance_id")
        .eq("id", input.workOrderId)
        .maybeSingle();
      if (wo?.appliance_id && UUID_RE.test(wo.appliance_id)) {
        resolvedApplianceId = wo.appliance_id;
      }
    }
    // 2) Upsert by model_number
    if (!UUID_RE.test(resolvedApplianceId) && input.modelNumber) {
      const { data: existing } = await supabase
        .from("appliances")
        .select("id")
        .eq("model_number", input.modelNumber)
        .eq("company_id", companyId)
        .eq("user_id", userId)
        .maybeSingle();
      if (existing?.id) {
        resolvedApplianceId = existing.id;
      } else {
        const { data: created, error: insErr } = await supabase
          .from("appliances")
          .insert({
            user_id: userId,
            company_id: companyId,
            model_number: input.modelNumber,
            name: input.applianceName ?? input.modelNumber,
            brand: input.brand ?? null,
            category: input.category ?? null,
            status: "pending",
          })
          .select("id")
          .single();
        if (insErr) {
          console.error("createPassport: failed to upsert appliance", insErr);
          throw new Error(insErr.message || "Failed to persist appliance for passport.");
        }
        resolvedApplianceId = created.id;
      }
    }
    if (!UUID_RE.test(resolvedApplianceId)) {
      throw new Error("Cannot generate passport: appliance has not been saved yet.");
    }
  }

  let lastError: any = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const serial = generateFixidSerial();
    const { data, error } = await supabase
      .from("product_passports")
      .insert({
        serial_number: serial,
        appliance_id: resolvedApplianceId,
        work_order_id: input.workOrderId ?? null,
        user_id: userId,
        company_id: companyId,
        workflow_type: input.workflowType,
        brand: input.brand ?? null,
        model_number: input.modelNumber ?? null,
        category: input.category ?? null,
        appliance_name: input.applianceName ?? null,
        quality_grade: input.qualityGrade ?? null,
        summary_data: input.summaryData ?? {},
      })
      .select()
      .single();
    if (!error && data) return data as ProductPassport;
    lastError = error;
    console.error(
      `createPassport: insert attempt ${attempt + 1} failed`,
      { code: error?.code, message: error?.message, details: error?.details, hint: error?.hint }
    );
    // 23505 = unique violation; retry only on serial collision
    if (error?.code !== "23505") break;
  }
  const msg =
    lastError?.message ||
    lastError?.details ||
    "Failed to create passport. Please try again.";
  throw new Error(msg);
}
